import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import {
  CircularProgress,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { FaPlusCircle } from "react-icons/fa";
import Toolbar from "../Toolbar/Toolbar.js";
import "./QuickDriftTable.css";
import apiService from "../../API/ApiService.js";

const QuickDriftTable = ({
  testerName,
  filteredData,
  onQuickDriftClick,
  totalCount,
  appliedFilters,
}) => {
  const [rows, setRows] = useState([]);
  const [totalRowCount, setTotalRows] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState([]);
const [visibleColumns, setVisibleColumns] = useState([
  "serial_number",
  "state",
  "Reason",
  "date",
  "quickDriftName",
]);
  const [openDialog, setOpenDialog] = useState(false);

  // ✅ Map backend fields to frontend-friendly keys
const mapBackendToFrontend = (data) =>
  data.map((item, index) => {
    const {
      quickdriftname,  // destructure to remove later
      ...rest
    } = item;
    return {
      id: item.id || index,
      state: item.state,
      reason: item.Reason || "",
      date: item.date ? new Date(item.date).toLocaleString() : "",
      quickDriftName: quickdriftname || "", // standardized key
      ...rest,
    };
  });

  // Generate columns dynamically
  const generateColumns = useCallback((data) => {
    if (!data || data.length === 0) return [];

    return Object.keys(data[0]).map((key) => {
      let headerName = key.replace(/_/g, " ").toUpperCase();

      if (key === "realSerialNumber") headerName = "Serial Number";
      if (key === "globalStatus") headerName = "State";

      return {
        field: key,
        headerName,
        flex: 1,
        minWidth: 120,
        maxWidth: 300,
        renderCell: (params) => (
          <Tooltip title={params.value || ""} arrow>
            <div
              style={{
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.value}
            </div>
          </Tooltip>
        ),
      };
    });
  }, []);

  // Fetch API data
  const fetchData = useCallback(
  async (newPage, newPageSize, filters = appliedFilters) => {
    if (!testerName) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        testerName, // ✅ key from props
        page: newPage + 1,
        pageSize: newPageSize === -1 ? totalRowCount : newPageSize,
        ...filters,
      };

      const response = await apiService.get(`/getQuickDriftSessionData/${testerName}`, { params });
      

      if (response?.data?.data) {
        const mappedRows = mapBackendToFrontend(response.data.data);
        setRows(mappedRows);
        setTotalRows(response.data.totalCount);
        setColumns(generateColumns(mappedRows));
      }
    } catch (error) {
     
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  },
  [testerName, appliedFilters, generateColumns, totalRowCount]
);

  // Load on mount or filter change
  useEffect(() => {
    if (filteredData?.length > 0) {
      const mappedRows = mapBackendToFrontend(filteredData);
      setRows(mappedRows);
      setColumns(generateColumns(mappedRows));
      setTotalRows(totalCount);
    } else {
      fetchData(page, pageSize, appliedFilters);
    }
  }, [
    filteredData,
    generateColumns,
    page,
    pageSize,
    totalCount,
    appliedFilters,
    fetchData,
  ]);

  // Handle column dialog
  const toggleColumnSelectionDialog = useCallback(() => {
    setOpenDialog((prev) => !prev);
  }, []);

  const handleColumnVisibilityChange = useCallback((columnField) => {
    setVisibleColumns((prev) =>
      prev.includes(columnField)
        ? prev.filter((col) => col !== columnField)
        : [...prev, columnField]
    );
  }, []);

  const sortedColumns = columns
    .filter((col) => visibleColumns.includes(col.field))
    .sort(
      (a, b) =>
        visibleColumns.indexOf(a.field) - visibleColumns.indexOf(b.field)
    );

  const handlePaginationModelChange = (newModel) => {
    const { pageSize: newPageSize, page: newPage } = newModel;
    setPage(newPage);
    setPageSize(newPageSize);
    fetchData(newPage, newPageSize, appliedFilters);
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Toolbar
        data={rows}
        visibleColumns={visibleColumns}
        filename="QuickDriftData"
        filters={appliedFilters}
      />

      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <Box sx={{ position: "absolute", right: 16, top: 8, zIndex: 1 }}>
          <IconButton onClick={toggleColumnSelectionDialog}>
            <FaPlusCircle size={24} />
          </IconButton>
        </Box>

        {/* ✅ Column selection dialog */}
        <Dialog open={openDialog} onClose={toggleColumnSelectionDialog}>
          <DialogTitle>Select Columns to Display</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {columns.map((col) => (
                <FormControlLabel
                  key={col.field}
                  control={
                    <Checkbox
                      checked={visibleColumns.includes(col.field)}
                      onChange={() => handleColumnVisibilityChange(col.field)}
                    />
                  }
                  label={col.headerName}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleColumnSelectionDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* ✅ DataGrid */}
        <DataGrid
          rows={rows}
          columns={sortedColumns}
          pagination
          paginationModel={{ page, pageSize }}
          pageSizeOptions={[5, 10, 20, 50, 100]}
          rowCount={totalRowCount}
          paginationMode="server"
          onPaginationModelChange={handlePaginationModelChange}
          slots={{
            loadingOverlay: CircularProgress,
            pagination: GridPagination,
          }}
                      sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                lineHeight: '1.2',
              },
              '& .MuiDataGrid-columnHeader': {
                padding: '0 8px',
              },
              '& .MuiDataGrid-columnHeaderTitleContainer': {
                width: '100%',
              },
            }}
            getCellClassName={(params) => {
              if (params.field === 'state') {
                return params.value === 'OK' ? 'cell-ok' : 
                       params.value === 'KO' ? 'cell-ko' : '';
              }
              return '';
            }}
          
          onRowClick={(params) => onQuickDriftClick(params.row)}
        />
      </Box>
    </Box>
  );
};

export default QuickDriftTable;
